import {
  Card,
  CardBody,
  CardHeader,
  Heading,
  VStack,
  HStack,
  Text,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Box,
} from '@chakra-ui/react'
import { Shield, AlertTriangle, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { Skeleton } from '@chakra-ui/react'

interface SecurityStatusCardProps {
  status: 'green' | 'yellow' | 'red'
  trend: 'increasing' | 'decreasing' | 'stable'
  failedLogins24h: number
  failedLoginsYesterday: number
  activeSessions: number
  lockedUsers: number
  disabledUsers: number
  isLoading?: boolean
}

export function SecurityStatusCard({
  status,
  trend,
  failedLogins24h,
  failedLoginsYesterday,
  activeSessions,
  lockedUsers,
  disabledUsers,
  isLoading = false,
}: SecurityStatusCardProps) {
  const getStatusColor = () => {
    switch (status) {
      case 'green':
        return 'green'
      case 'yellow':
        return 'yellow'
      case 'red':
        return 'red'
      default:
        return 'gray'
    }
  }

  const getStatusLabel = () => {
    switch (status) {
      case 'green':
        return 'Secure'
      case 'yellow':
        return 'Warning'
      case 'red':
        return 'Critical'
      default:
        return 'Unknown'
    }
  }

  const getTrendIcon = () => {
    switch (trend) {
      case 'increasing':
        return <TrendingUp size={16} />
      case 'decreasing':
        return <TrendingDown size={16} />
      default:
        return <Minus size={16} />
    }
  }

  const getTrendColor = () => {
    switch (trend) {
      case 'increasing':
        return 'red.500'
      case 'decreasing':
        return 'green.500'
      default:
        return 'gray.500'
    }
  }

  const calculateChange = () => {
    if (failedLoginsYesterday === 0) {
      return failedLogins24h > 0 ? 100 : 0
    }
    return ((failedLogins24h - failedLoginsYesterday) / failedLoginsYesterday) * 100
  }

  const change = calculateChange()
  const changeText = change > 0 ? `+${change.toFixed(0)}%` : `${change.toFixed(0)}%`

  return (
    <Card bg="white" borderRadius="md" boxShadow="sm">
      <CardHeader pb={2}>
        <HStack justify="space-between" align="center">
          <Heading size="md">Security Status</Heading>
          {isLoading ? (
            <Skeleton height="24px" width="80px" />
          ) : (
            <Badge colorScheme={getStatusColor()} fontSize="sm" px={3} py={1}>
              <HStack spacing={1}>
                {status === 'green' && <Shield size={14} />}
                {status !== 'green' && <AlertTriangle size={14} />}
                <Text>{getStatusLabel()}</Text>
              </HStack>
            </Badge>
          )}
        </HStack>
      </CardHeader>
      <CardBody pt={0}>
        <VStack spacing={4} align="stretch">
          {/* Failed Logins */}
          <Box>
            <Stat>
              <StatLabel fontSize="xs" color="gray.600">
                Failed Logins (24h)
              </StatLabel>
              {isLoading ? (
                <Skeleton height="24px" width="60px" mt={1} />
              ) : (
                <HStack spacing={2} align="baseline">
                  <StatNumber fontSize="xl" fontWeight="bold">
                    {failedLogins24h.toLocaleString()}
                  </StatNumber>
                  <StatHelpText mb={0} fontSize="xs" color={getTrendColor()}>
                    <HStack spacing={1}>
                      {getTrendIcon()}
                      <Text>{changeText}</Text>
                    </HStack>
                  </StatHelpText>
                </HStack>
              )}
              <StatHelpText mb={0} fontSize="xs" color="gray.500">
                Yesterday: {failedLoginsYesterday.toLocaleString()}
              </StatHelpText>
            </Stat>
          </Box>

          {/* Other Metrics */}
          <HStack spacing={4} flexWrap="wrap">
            <Box flex={1} minW="120px">
              <Text fontSize="xs" color="gray.600" mb={1}>
                Active Sessions
              </Text>
              {isLoading ? (
                <Skeleton height="20px" width="40px" />
              ) : (
                <Text fontSize="lg" fontWeight="bold">
                  {activeSessions.toLocaleString()}
                </Text>
              )}
            </Box>
            <Box flex={1} minW="120px">
              <Text fontSize="xs" color="gray.600" mb={1}>
                Locked Users
              </Text>
              {isLoading ? (
                <Skeleton height="20px" width="40px" />
              ) : (
                <Text fontSize="lg" fontWeight="bold" color={lockedUsers > 0 ? 'orange.500' : 'gray.500'}>
                  {lockedUsers.toLocaleString()}
                </Text>
              )}
            </Box>
            <Box flex={1} minW="120px">
              <Text fontSize="xs" color="gray.600" mb={1}>
                Disabled Users
              </Text>
              {isLoading ? (
                <Skeleton height="20px" width="40px" />
              ) : (
                <Text fontSize="lg" fontWeight="bold" color={disabledUsers > 0 ? 'yellow.500' : 'gray.500'}>
                  {disabledUsers.toLocaleString()}
                </Text>
              )}
            </Box>
          </HStack>
        </VStack>
      </CardBody>
    </Card>
  )
}
